// Available Endorsements Response (POST applications/availableEndorsements) - EPLI has options object
// {
//   "availableEndorsements": {
//     "epliCoverage": "{\"limits\":\"50000/50000\",\"retention\":\"10000\"}",
//   },
//   "options": {
//     "epliCoverage": {
//       "limitsOptions": [
//         "50000/50000",
//         "100000/100000",
//         "250000/250000"
//       ],
//      "retentionOptions": [
//        "10000"
//      ],
//      "recommendedLimits": "50000/50000",
//      "recommendedRetention": "10000"
//   }
// }
// }
//
// Application Update Payload (POST applications)
// {
//   "endorsements": {
//     epliCoverage: {
//       limits: '50000/50000',
//       retention: '10000'
//     },
//   }
// }
import { useSelector, useDispatch } from 'react-redux';
import { setEPLICoverage, setCheckedEndorsement } from '../../../../actions/application';
import { selectEPLICoverageLimit, selectEPLIRetentionLimit, selectCheckedEndorsements } from '../../../../selectors/application';
import { getAvailableEndorsementsOptions } from '../../../../selectors/availableEndorsements';
import { selectLoadingState } from '../../../../selectors';
import styled from 'styled-components';
import EndorsementCard from '../../EndorsementCard';
import UnderwritingRadioGroup from '../../shared/UnderwritingRadioGroup';
import CenteredLoadingSpinner from '../../../AddOns/shared/CenteredLoadingSpinner';
import { config } from './EPLI.config';

const { endorsementKey: ENDORSEMENT_KEY, label: LABEL, description: DESCRIPTION } = config;

const LIMITS_LABELS: Record<string, string> = {
  '50000/50000': '$50,000 per claim / $50,000 aggregate',
  '100000/100000': '$100,000 per claim / $100,000 aggregate',
  '250000/250000': '$250,000 per claim / $250,000 aggregate',
};

const RETENTION_LABELS: Record<string, string> = {
  '2500': '$2,500',
  '5000': '$5,000',
  '10000': '$10,000',
};

const EPLI = () => {
  const dispatch = useDispatch();
  const availableOptions = useSelector(getAvailableEndorsementsOptions);
  const isQuoteLoading = useSelector(selectLoadingState);
  const selectedCoverageLimit = useSelector(selectEPLICoverageLimit);
  const selectedRetentionLimit = useSelector(selectEPLIRetentionLimit);
  const isSelected = useSelector(selectCheckedEndorsements).includes(ENDORSEMENT_KEY);

  const apiLimitsOptions = availableOptions?.[ENDORSEMENT_KEY]?.limitsOptions || [];
  const limitsOptions = apiLimitsOptions.map((value: string) => ({
    label: LIMITS_LABELS[value] || value,
    value,
  }));

  const apiRetentionOptions = availableOptions?.[ENDORSEMENT_KEY]?.retentionOptions || [];
  const retentionOptions = apiRetentionOptions.map((value: string) => ({
    label: RETENTION_LABELS[value] || value,
    value,
  }));

  const epliOptions = availableOptions?.[ENDORSEMENT_KEY];
  const recommendedLimits = epliOptions?.recommendedLimits;
  const recommendedRetention = epliOptions?.recommendedRetention;

  const handleCheckboxChange = (checked: boolean) => {
    dispatch(setCheckedEndorsement(ENDORSEMENT_KEY, checked));

    const defaultEPLIOptions = {
      limits: recommendedLimits || limitsOptions[0]?.value,
      retention: recommendedRetention || retentionOptions[0]?.value,
    };

    dispatch(setEPLICoverage(checked ? defaultEPLIOptions : null));
  };

  const handleLimitsChange = (newValue: string) => {
    dispatch(setEPLICoverage({ limits: newValue }));
  };

  const handleRetentionChange = (newValue: string) => {
    dispatch(setEPLICoverage({ retention: newValue }));
  };

  return (
    <EndorsementCard
      endorsementKey={ENDORSEMENT_KEY}
      label={LABEL}
      description={DESCRIPTION}
      isSelected={isSelected}
      onCheckboxChange={handleCheckboxChange}
      hasExpandableContent
    >
      <SubQuestionsContainer>
        {isQuoteLoading && <CenteredLoadingSpinner useAltColor />}
        <UnderwritingRadioGroup
          questionCopy="Select your coverage limit:"
          singleOptionCopy="Coverage limit"
          options={limitsOptions}
          selectedValue={selectedCoverageLimit}
          onChange={handleLimitsChange}
          name="epli-limits"
          isActive={!isQuoteLoading}
        />
        <UnderwritingRadioGroup
          questionCopy="Select your retention limit:"
          singleOptionCopy="Retention limit"
          options={retentionOptions}
          selectedValue={selectedRetentionLimit}
          onChange={handleRetentionChange}
          name="epli-retention"
          isActive={!isQuoteLoading}
        />
      </SubQuestionsContainer>
    </EndorsementCard>
  );
};

const SubQuestionsContainer = styled.div`
  position: relative;
`;

export default EPLI;
